import { InvoiceEvent } from '@/types'
import { formatInvoiceReason, formatInvoiceStatus, InvoiceStatusTone, invoiceStatusTone } from '@/utils/format'
import styles from '@/components/invoices/InvoiceTimeline.module.css'
import { Loader, Text, ThemeIcon, Timeline } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { CSSProperties, useState } from 'react'

const ARRIVAL_STAGGER_MS = 180

function eventDetail(event: InvoiceEvent) {
  // Payment events carry their amount in the text the sync builds ("Encaissée · 120,00 €").
  if (event.status_code === 'fr:212' && event.status_text?.includes('€')) return event.status_text
  return null
}

const TONES: Record<InvoiceStatusTone, { color: string; icon: typeof IconCheck }> = {
  done: { color: 'green', icon: IconCheck },
  warning: { color: 'orange', icon: IconAlertTriangle },
  failure: { color: 'red', icon: IconX },
}

function StatusBullet({ tone, style }: { tone: InvoiceStatusTone; style?: CSSProperties }) {
  const { color, icon: Icon } = TONES[tone]

  return (
    <ThemeIcon color={color} radius="xl" size={22} className={styles.bullet} style={style}>
      <Icon size={14} stroke={3} />
    </ThemeIcon>
  )
}

function PendingBullet() {
  return (
    <ThemeIcon variant="light" color="blue" radius="xl" size={22} className={styles.pendingBullet}>
      <Loader size={12} color="blue" />
    </ThemeIcon>
  )
}

// +pendingLabel+ names the step the page is waiting for while it follows a freshly sent invoice. Statuses that
// arrive after the page opened slide in one after the other; the ones already there stay still.
export default function InvoiceTimeline({
  events,
  pendingLabel,
}: {
  events: InvoiceEvent[]
  pendingLabel?: string | null
}) {
  const [initialIds] = useState(() => new Set(events.map((event) => event.id)))
  const ordered = [...events].reverse()
  const arrivedIds = events.filter((event) => !initialIds.has(event.id)).map((event) => event.id)

  const arrivalStyle = (event: InvoiceEvent): CSSProperties | undefined => {
    const position = arrivedIds.indexOf(event.id)
    return position < 0 ? undefined : { animationDelay: `${position * ARRIVAL_STAGGER_MS}ms` }
  }

  if (ordered.length === 0 && !pendingLabel) {
    return (
      <Text size="sm" c="dimmed">
        Aucun statut reçu pour l'instant.
      </Text>
    )
  }

  return (
    <Timeline bulletSize={22} lineWidth={2}>
      {pendingLabel && (
        <Timeline.Item
          key="pending"
          className={styles.pending}
          bullet={<PendingBullet />}
          title={<span className={styles.pendingTitle}>{`${pendingLabel}…`}</span>}
        >
          <Text size="xs" c="dimmed">
            Suivi en direct
          </Text>
        </Timeline.Item>
      )}
      {ordered.map((event) => (
        <Timeline.Item
          key={event.id}
          className={arrivedIds.includes(event.id) ? styles.arrived : undefined}
          style={arrivalStyle(event)}
          bullet={
            <StatusBullet tone={invoiceStatusTone(event.status_code, event.status_text)} style={arrivalStyle(event)} />
          }
          title={formatInvoiceStatus(event.status_code)}
        >
          <Text size="xs" c="dimmed">
            {dayjs(event.occurred_at).format('DD/MM/YYYY HH:mm')}
          </Text>
          {eventDetail(event) && <Text size="sm">{eventDetail(event)}</Text>}
          {event.reason && (
            <Text size="sm" c="red.8">
              {formatInvoiceReason(event.reason)}
            </Text>
          )}
        </Timeline.Item>
      ))}
    </Timeline>
  )
}
